import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import html2pdf from "html2pdf.js";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
// import GoogleDrive from "../DashboardComponents/GoogleDrive";
// import { FaDownload } from "react-icons/fa6";
import { ChangePassword } from "../HomeComponents/ChangePassword";
// import Tippy from "@tippyjs/react"; // Tooltip for inline previews on hover
// import "tippy.js/dist/tippy.css";
import "../../styles/DashboardStyling/ElasticSearch.css";

const ElasticSearch = ({
  isLoggedIn,
  setIsLoggedIn,
  userData,
  setUserData,
  selectedSponsor,
  selectedProject,
  setSelectedProject,
  setSelectedSponsor,
  onSponsorChange,
}) => {
  const ElasticInfo = `${process.env.PUBLIC_URL}/assets/logos/elastic-info.svg`;
  const WorkManagement = `${process.env.PUBLIC_URL}/assets/logos/work-management.svg`;
  const SendQuery = `${process.env.PUBLIC_URL}/assets/logos/send-query.svg`;
  const StopQuery = `${process.env.PUBLIC_URL}/assets/logos/stop-query.svg`;
  const PanBottom = `${process.env.PUBLIC_URL}/assets/logos/pan-bottom.svg`;
  const Download = `${process.env.PUBLIC_URL}/assets/logos/download.svg`;
  // const RegenerateResponse = `${process.env.PUBLIC_URL}/assets/logos/regenerate-response.svg`;
  const NextPage = `${process.env.PUBLIC_URL}/assets/logos/next-page.svg`;
  const NextPageDark = `${process.env.PUBLIC_URL}/assets/logos/next-page-dark.svg`;
  const Document = `${process.env.PUBLIC_URL}/assets/logos/document.svg`;
  // const PreviousPage = `${process.env.PUBLIC_URL}/assets/logos/previous-page.svg`;

  // The conversation array holds objects: { question, answer, documents: [{ filename, link }] }
  const [conversation, setConversation] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [followUpSuggestions, setFollowUpSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showDriveModal, setShowDriveModal] = useState(false);
  // (Commented) Token count state for tracking tokens during generation
  // const [tokenCount, setTokenCount] = useState(0);
  // const [tokenTotal, setTokenTotal] = useState(0);

  const navigate = useNavigate();

  // A ref to auto-scroll to the bottom
  const messagesEndRef = useRef(null);

  // ==================== REFS AND STATES FOR ENHANCED AUTO-SCROLL ====================
  const conversationContainerRef = useRef(null);
  const [autoScroll, setAutoScroll] = useState(true);
  // This ref tracks if the user has manually scrolled up.
  const userScrolledUpRef = useRef(false);

  // ==================== REFS FOR CANCELLATION ====================
  const abortControllerRef = useRef(null);
  const cancelGenerationRef = useRef(false);

  // Scroll to the bottom of the conversation
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Whenever conversation or suggestions update, scroll if autoScroll is true
  useEffect(() => {
    if (autoScroll) {
      // scrollToBottom();
    }
  }, [conversation, followUpSuggestions, autoScroll]);

  // Attach a scroll listener to detect manual scrolling
  useEffect(() => {
    const container = conversationContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
      // If the user is more than 50px away from the bottom, mark as manually scrolled
      if (distanceFromBottom > 50) {
        userScrolledUpRef.current = true;
        setAutoScroll(false);
      } else {
        // Only re-enable autoScroll if the user hasn't manually scrolled up previously
        if (!userScrolledUpRef.current) {
          setAutoScroll(true);
        }
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  // ===== RESET CONVERSATION ON SPONSOR/PROJECT CHANGE =====
  useEffect(() => {
    // Cancel any ongoing response generation when switching sponsor or project
    if (loading) {
      cancelGenerationRef.current = true;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    }
    setConversation([]);
    setFollowUpSuggestions([]);
    setQuery("");
  }, [selectedSponsor, selectedProject]);

  const handleLogout = async () => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/logout`,
        {},
        { withCredentials: true }
      );
      setUserData(null);
      setIsLoggedIn(false);
      setShowDropdown(false);
      navigate("/");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  // ==================== CANCEL GENERATION FUNCTION ====================
  const cancelGeneration = () => {
    if (loading) {
      cancelGenerationRef.current = true;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    }
  };

  // ========== MAIN SEARCH HANDLER ==========
  const handleSearch = async (customQuery) => {
    if (!customQuery && !query.trim()) return;
    if (loading) return; // Prevent overlapping calls

    // Reset cancellation flag and set up AbortController
    cancelGenerationRef.current = false;
    abortControllerRef.current = new AbortController();

    // Reset user scroll flag so auto-scroll re-enables at the start of generation
    userScrolledUpRef.current = false;
    setAutoScroll(true);

    const userQuery = customQuery !== undefined ? customQuery : query;
    setLoading(true);
    setFollowUpSuggestions([]);

    // 1) Add new conversation turn with blank answer and force immediate scrolling
    const newTurnIndex = conversation.length;
    setConversation((prev) => [
      ...prev,
      {
        question: userQuery,
        answer: "",
        documents: [],
        showAllDocs: false,
        // Store pagination page in each turn
        paginationPage: 1,
      },
    ]);
    // Immediately pan to the bottom for new turn.
    scrollToBottom();

    setQuery(""); // clear the input

    try {
      // 2) Call the backend with abort signal
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/query_es`,
        {
          query: userQuery,
          selectedSponsor,
          selectedProject,
        },
        { signal: abortControllerRef.current.signal }
      );

      const answerText = res.data.response || "";
      const docs = res.data.documents || [];
      const suggestions = res.data.suggestions || [];

      // 3) Type out the answer, character by character
      // (Commented) Initialize token counter for this answer
      // setTokenCount(0);
      // setTokenTotal(answerText.length);
      let typedAnswer = "";
      for (let i = 0; i < answerText.length; i++) {
        // If cancellation was requested, stop typing and mark the generation as stopped.
        if (cancelGenerationRef.current) {
          setConversation((prev) => {
            const updated = [...prev];
            if (updated[newTurnIndex]) {
              // Append a note indicating generation was stopped (preserve content typed so far)
              // Ensure proper markdown closure before appending stop message.
              let haltedAnswer = typedAnswer;
              if ((haltedAnswer.match(/```/g) || []).length % 2 === 1) {
                haltedAnswer += "\n```";
              }
              if (
                (haltedAnswer.replace(/```/g, "").match(/`/g) || []).length %
                  2 ===
                1
              ) {
                haltedAnswer += "`";
              }
              updated[newTurnIndex].answer =
                haltedAnswer + " [Generation stopped]";
            }
            return updated;
          });
          return;
        }
        typedAnswer += answerText[i];
        // Prepare a safe version of the answer for display to avoid broken markdown formatting
        let displayAnswer = typedAnswer;
        // If an odd number of triple backticks are present, add a closing triple backtick
        if ((displayAnswer.match(/```/g) || []).length % 2 === 1) {
          displayAnswer += "\n```";
        }
        // If an odd number of single backticks (inline code) are present (excluding those in triples), add a closing backtick
        if (
          (displayAnswer.replace(/```/g, "").match(/`/g) || []).length % 2 ===
          1
        ) {
          displayAnswer += "`";
        }
        // Update conversation state with the new character (and any temporary formatting closures)
        setConversation((prev) => {
          const updated = [...prev];
          if (updated[newTurnIndex]) {
            updated[newTurnIndex].answer = displayAnswer;
          }
          return updated;
        });
        // Add a small delay for the typing animation
        await new Promise((r) => setTimeout(r, 20));
        // If autoScroll is still true, keep scrolling with each new character
        if (autoScroll) {
          scrollToBottom();
        }
        // (Commented) Optionally update token/character counter
        // setTokenCount(i + 1);
      }

      // 4) After the typing is complete, attach the documents
      setConversation((prev) => {
        const updated = [...prev];
        if (updated[newTurnIndex]) {
          updated[newTurnIndex].documents = docs;
        }
        return updated;
      });

      // 5) Store follow-up suggestions
      setFollowUpSuggestions(suggestions);
      // (Commented) Reset or utilize token counts if needed after generation
      // setTokenCount(answerText.length);
      // setTokenTotal(0);
    } catch (error) {
      // If the request was cancelled, mark the turn as stopped (if not already handled above)
      if (axios.isCancel(error) || error.name === "CanceledError") {
        setConversation((prev) => {
          const updated = [...prev];
          if (updated[newTurnIndex] && !updated[newTurnIndex].answer) {
            updated[newTurnIndex].answer = "Generation stopped.";
          }
          return updated;
        });
      } else {
        // On error, show an error message in the conversation
        setConversation((prev) => {
          const updated = [...prev];
          if (updated[newTurnIndex]) {
            updated[newTurnIndex].answer = "Error fetching results.";
          }
          return updated;
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // ==================== PAGINATION HANDLERS ====================
  const handleNextPage = (turnIndex) => {
    setConversation((prev) => {
      const updated = [...prev];
      const turn = updated[turnIndex];
      if (turn) {
        const totalPages = Math.ceil(turn.documents.length / 3);
        if (turn.paginationPage < totalPages) {
          turn.paginationPage += 1;
        }
      }
      return updated;
    });
  };

  const handlePrevPage = (turnIndex) => {
    setConversation((prev) => {
      const updated = [...prev];
      const turn = updated[turnIndex];
      if (turn && turn.paginationPage > 1) {
        turn.paginationPage -= 1;
      }
      return updated;
    });
  };

  const downloadConversationAsPDF = () => {
    const element = conversationContainerRef.current;
    if (!element) {
      console.error("Conversation container element not found");
      return;
    }
    const opt = {
      margin: 0.5,
      filename: "LWC_Conversation.pdf",
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, opacity: 1 },
      jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
    };
  
    html2pdf().set(opt).from(element).save();
  };  

  // Helper function to get the display text for sponsor/project
  const getSponsorProjectDisplay = () => {
    if (selectedSponsor === "All" && selectedProject === "All") {
      return (
        <div className="initial-sponsor-project">
          <h2>Welcome to LWC Project Explorer</h2>
        </div>
      );
    }
    return (
      <div className="initial-sponsor-project">
        <h2>
          {selectedSponsor === "All"
            ? "Welcome to LWC Project Explorer"
            : selectedSponsor}
        </h2>
        {selectedSponsor !== "All" && selectedProject === "All" && (
          <h3>Please Select a Project to Continue</h3>
        )}
        <h3>{selectedProject === "All" ? null : selectedProject}</h3>
      </div>
    );
  };

  const toggleShowDocs = (index) => {
    setConversation((prev) => {
      const updated = [...prev];
      if (updated[index]) {
        updated[index].showAllDocs = true;
        // Default active page = 2 upon expansion (show new docs first)
        updated[index].paginationPage = 1;
      }
      return updated;
    });
  };

  // ========== RENDER UI ==========
  return (
    <div className="elastic-search-container">
      <div className="elastic-search-div">
        {/* Header */}
        <div className="elastic-search-header">
          <div className="elastic-search-header-div">
            {conversation.length > 0 && (
              <div className="pdf-button-wrapper">
                <button
                  // type="submit"
                  className="user-btn"
                  onClick={downloadConversationAsPDF}
                  title="Download Chat as PDF"
                  // style={{ width: "5vh", height: "5vh" }}
                >
                  <img
                    src={Download}
                    alt="Workspace Management"
                    className="workspace-icon"
                  />
                </button>
              </div>
            )}
            <div className="header-column actions">
              {!isLoggedIn && !userData ? (
                <>{/* Your login form code here */}</>
              ) : (
                <>
                  <div
                    className="user-dropdown"
                    onMouseLeave={() => setShowDropdown(false)}
                  >
                    <button
                      className="user-btn"
                      title="User Control Panel"
                      onClick={() => setShowDropdown(!showDropdown)}
                    >
                      {userData?.FName?.[0]}
                      {userData?.LName?.[0]}
                    </button>
                    {showDropdown && (
                      <div
                        className="dropdown-menu"
                        style={{
                          right: `${
                            userData?.Role.includes("Admin")
                              ? "calc(14vh + 30px)"
                              : "calc(7vh + 20px)"
                          }`,
                        }}
                      >
                        <button onClick={() => setShowChangePassword(true)}>
                          Change Password
                        </button>
                        <button onClick={handleLogout}>Log Out</button>
                      </div>
                    )}
                  </div>
                  <button className="user-btn" style={{ marginLeft: "10px" }} onClick={() => navigate("/learn-more")}
                  >
                    <img
                      src={ElasticInfo}
                      alt="Workspace Management"
                      className="workspace-icon"
                      title="Learn More Elastic Search"
                    />
                  </button>
                </>
              )}
              {isLoggedIn && userData?.Role.includes("Admin") && (
                <button
                  className="workspace-btn"
                  title="Admin Control Panel"
                  onClick={() => navigate("/admin")}
                >
                  <img
                    src={WorkManagement}
                    alt="Workspace Management"
                    className="workspace-icon"
                  />
                </button>
              )}
              {showChangePassword && (
                <div className="modal-overlay">
                  <div className="change-password-modal-content">
                    <ChangePassword
                      email={userData?.Email}
                      setShowChangePassword={setShowChangePassword}
                      onBack={() => setShowChangePassword(false)}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Initial state if no conversation */}
        {conversation.length === 0 && (
          <div className="initial-state">
            {getSponsorProjectDisplay()}
            {selectedSponsor !== "All" && selectedProject !== "All" &&  (
              <div className="starter-questions">
                <p>Try one of these questions to get started:</p>
                <button
                  className="starter-question-btn"
                  type="submit"
                  onClick={() =>
                    handleSearch(
                      selectedProject === "All"
                        ? "What are some overarching goals across all projects sponsored by this organization?"
                        : "What is the overarching purpose and scope of this project?"
                    )
                  }
                >
                  {selectedProject === "All"
                    ? "What are some overarching goals across all projects sponsored by this organization?"
                    : "What is the overarching purpose and scope of this project?"}
                </button>
                <button
                  type="submit"
                  className="starter-question-btn"
                  onClick={() =>
                    handleSearch(
                      selectedProject === "All"
                        ? `Which key research findings apply broadly to all projects under ${selectedSponsor}?`
                        : "Which research trends and findings emerged as most influential in guiding the project's direction?"
                    )
                  }
                >
                  {selectedProject === "All"
                    ? `Which key research findings apply broadly to all projects under ${selectedSponsor}?`
                    : "Which research trends and findings emerged as most influential in guiding the project's direction?"}
                </button>
                <button
                  type="submit"
                  className="starter-question-btn"
                  onClick={() =>
                    handleSearch(
                      selectedProject === "All"
                        ? "What major opportunities might be missed across all projects, and why were they not pursued?"
                        : "What were the major opportunities identified that ultimately were not pursued, and why?"
                    )
                  }
                >
                  {selectedProject === "All"
                    ? "What major opportunities might be missed across all projects, and why were they not pursued?"
                    : "What were the major opportunities identified that ultimately were not pursued, and why?"}
                </button>
              </div>
            )}
          </div>
        )}
        {/* Conversation History */}
        <div className="conversation-container" ref={conversationContainerRef}>
          <div className="es-placeholder">
            {conversation.map((turn, idx) => (
              <div
                key={idx}
                className="turn"
                style={
                  loading
                    ? { borderBottom: "none" }
                    : { borderBottom: "2px solid #EAEAEA" }
                }
              >
                <div className="user-question">{turn.question}</div>
                <div className="chat-response">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {turn.answer}
                  </ReactMarkdown>
                </div>
                {turn.documents && turn.documents.length > 0 && (
                  <div className="document-list">
                    <h3 style={{ marginBottom: "15px" }}>Relevant Documents</h3>
                    {turn.showAllDocs ? (
                      <>
                        {(() => {
                          const totalPages = Math.ceil(
                            turn.documents.length / 3
                          );
                          const startIdx = (turn.paginationPage - 1) * 3;
                          const endIdx = turn.paginationPage * 3;
                          const docsOnPage = turn.documents.slice(
                            startIdx,
                            endIdx
                          );
                          return (
                            <>
                              <ul>
                                {docsOnPage.map((docObj, j) => {
                                  if (typeof docObj === "string") {
                                    return (
                                      <button
                                        key={j}
                                        // type="submit"
                                        className="document-path"
                                      >
                                        {docObj}
                                      </button>
                                    );
                                  } else {
                                    const { filename, link } = docObj;
                                    return (
                                      <button
                                        key={j}
                                        // type="submit"
                                        className="document-path"
                                      >
                                        {/* Tooltip on document link */}
                                        {/* <Tippy content={filename}> */}
                                        <img
                                          src={Document}
                                          alt="Regenerate Response"
                                          className="workspace-icon"
                                        />
                                        <a
                                          href={link}
                                          target="_blank"
                                          className="document-filename"
                                          rel="noopener noreferrer"
                                        >
                                          {filename}
                                        </a>
                                        {/* </Tippy> */}
                                      </button>
                                    );
                                  }
                                })}
                              </ul>
                              <div className="pagination-controls">
                                <div className="pagination-controls-div">
                                  <img
                                    src={turn.paginationPage !== 1 ? NextPageDark : NextPage}
                                    onClick={() => handlePrevPage(idx)}
                                    alt="Previous Page"
                                    className="workspace-icon"
                                    style={{ transform: "scaleX(-1)" }}
                                    />
                                  <span style={{ margin: "0 10px" }}>
                                    Page {turn.paginationPage}/{totalPages}
                                  </span>
                                  <img
                                    src={turn.paginationPage !== totalPages ? NextPageDark : NextPage}
                                    onClick={() => handleNextPage(idx)}
                                    alt="Next Page"
                                    className="workspace-icon"
                                  />
                                </div>
                                {/* Regenerate Answer for completed response */}
                                {/* {!loading && turn.answer && (
                                  <button
                                    className="user-btn"
                                    title="Regenerate Response"
                                    onClick={() => handleSearch(turn.question)}
                                  >
                                    <img
                                      src={RegenerateResponse}
                                      alt="Regenerate Response"
                                      className="workspace-icon"
                                    />
                                  </button>
                                )} */}
                              </div>
                            </>
                          );
                        })()}
                      </>
                    ) : (
                      <>
                        <ul>
                          {turn.documents.slice(0, 3).map((docObj, j) => {
                            if (typeof docObj === "string") {
                              return (
                                <button
                                  key={j}
                                  // type="submit"
                                  className="document-path"
                                >
                                  {docObj}
                                </button>
                              );
                            } else {
                              const { filename, link } = docObj;
                              return (
                                <button
                                  key={j}
                                  // type="submit"
                                  className="document-path"
                                >
                                  {/* Tooltip on document link */}
                                  {/* <Tippy content={filename}> */}
                                  <img
                                    src={Document}
                                    alt="Regenerate Response"
                                    className="workspace-icon"
                                  />
                                  <a
                                    href={link}
                                    target="_blank"
                                    className="document-filename"
                                    rel="noopener noreferrer"
                                  >
                                    {filename}
                                  </a>
                                  {/* </Tippy> */}
                                </button>
                              );
                            }
                          })}
                        </ul>
                        <div className="pagination-controls">
                          <div className="pagination-controls-div">
                            {turn.documents.length > 3 && (
                              <span
                                className="show-more-btn"
                                onClick={() => toggleShowDocs(idx)}
                              >
                                Show More
                              </span>
                            )}
                          </div>
                          {/* Regenerate Answer for completed response */}
                          {/* {!loading && turn.answer && (
                            <button
                              className="user-btn"
                              title="Regenerate Response"
                              onClick={() => handleSearch(turn.question)}
                            >
                              <img
                                src={RegenerateResponse}
                                alt="Regenerate Response"
                                className="workspace-icon"
                              />
                            </button>
                          )} */}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
            {followUpSuggestions.length > 0 && (
              <div className="follow-up-suggestions">
                <p>
                  <em>Suggested follow-up questions:</em>
                </p>
                {followUpSuggestions.map((sugg, i) => (
                  <button
                    key={i}
                    type="submit"
                    className="suggestion-button"
                    disabled={loading}
                    onClick={() => handleSearch(sugg)}
                  >
                    {sugg}
                  </button>
                ))}
              </div>
            )}
            {loading && (
              <>
                <p className="loading-text">Fetching results... Please wait</p>
                {/* (Commented) Token count indicator during typing */}
                {/* <p className="token-counter">Characters: {tokenCount}/{tokenTotal}</p> */}
              </>
            )}
            <div ref={messagesEndRef} />
            {/* NEW: Pan to Bottom Button - visible if not auto-scrolling */}
          </div>
        </div>
        {/* Query Input */}
        <div className="query-input-div">
          <div className="query-input">
            {!autoScroll && (
              <button
                className="pan-to-bottom-btn"
                onClick={() => {
                  // Reset manual scroll flag and auto-scroll back to bottom
                  userScrolledUpRef.current = false;
                  setAutoScroll(true);
                  scrollToBottom();
                }}
              >
                <img src={PanBottom} alt="Pan to bottom" />
              </button>
            )}
            <textarea
              type="text"
              className="query-input-area"
              placeholder={
                conversation.length > 0
                  ? "Follow Up Questions?"
                  : "Ask anything about this project. Remember, word your questions thoughtfully."
              }
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSearch();
                }
              }}
            />
            <button
              className="send-query-btn"
              onClick={() => {
                if (loading) {
                  cancelGeneration();
                } else {
                  handleSearch();
                }
              }}
              title={loading ? "Stop Generating" : "Generate Response"}
            >
              {loading ? (
                <img
                  src={StopQuery}
                  alt="Stop Generation"
                  className="workspace-icon"
                />
              ) : (
                <img
                  src={SendQuery}
                  alt="Send Query"
                  className="workspace-icon"
                />
              )}
            </button>
          </div>
        </div>
        {/* Google Drive Indexing Modal */}
        {showDriveModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <button
                className="close-btn"
                onClick={() => setShowDriveModal(false)}
              >
                ❌ Close
              </button>
              {/* <GoogleDrive /> */}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ElasticSearch;
